<template>
    <v-layout justify-center>
        <v-responsive max-width="1680px" width="100%" height="100%">
            <v-row class="mt-8" justify="center" align="center">
                <v-col cols="auto" class="pt-0 pb-0 headline text-start">안심계약 목록</v-col>
                <v-spacer />
                <v-col cols="auto" class="pt-0 pb-0">
                    <v-btn class="ma-2" color="secondary" rounded @click="$refs['list-search'].show = !$refs['list-search'].show"><v-icon small class="mr-2">mdi-filter-variant</v-icon>검색필터</v-btn>
                </v-col>
            </v-row>

            <list-search v-model="filter" ref="list-search" />

            <list-excel />

            <v-data-table :headers="contractsHeaders" :items="contracts" disable-sort disable-pagination hide-default-footer class="elevation-1 mt-4">
                <template v-slot:[`item.isWhole`]="{ item }">
                    {{ item.isWhole ? "종합" : "부분" }}
                </template>
                <template v-slot:[`item.latestDocument.signsAt`]="{ item }">
                    <span v-if="item.documents.length">
                        {{ item.latestDocument.signsAt.toDate() }}
                    </span>
                    <span v-else>-</span>
                </template>
                <template v-slot:[`item.meta.employer.phone`]="{ item }">
                    {{ (item.meta.employer.phone || "").phoneNumberFormat() }}
                </template>
                <template v-slot:[`item.virtualAccount`]="{ item }">
                    {{ getVirtualAccount(item) }}
                </template>
                <template v-slot:[`item.taxProofStatus`]="{ item }">
                    {{ getTaxProofStatus(item) }}
                </template>
                <template v-slot:[`item.settlementStatus`]="{ item }">
                    {{ getSettlementStatus(item) }}
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-icon small @click="edit(item)"> mdi-pencil </v-icon>
                </template>
            </v-data-table>

            <v-pagination v-model="page" :length="pageCount" :total-visible="11" class="mt-4 mb-12 mx-4" @input="search"></v-pagination>

            <v-btn v-if="isConsole" bottom color="accent" fab fixed right @click="create()">
                <v-icon>mdi-pencil</v-icon>
            </v-btn>
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";
import ListSearch from "@/components/console/contracts/list-search.vue";
import ListExcel from "@/components/console/contracts/list-excel.vue";

export default {
    components: { ListSearch, ListExcel },
    data() {
        return {
            // filter related
            filter: {
                isWhole: this.$route.query.isWhole ? JSON.parse(this.$route.query.isWhole) : undefined,
                searchKey: this.$route.query.searchKey,
                searchValue: this.$route.query.searchValue,
            },

            categories: [],

            // pagination related
            page: +this.$route.query.page || 1,
            pageCount: 0,
            limit: 10,

            // data-table related
            contractsHeaders: [
                {
                    text: "구분",
                    value: "isWhole",
                    width: 60,
                },
                {
                    text: "계약번호",
                    value: "contractNumber",
                    width: 120,
                },
                {
                    text: "계약진행일",
                    value: "latestDocument.signsAt",
                    width: 120,
                },
                {
                    text: "계약서 상태",
                    value: "eSignStatus",
                    width: 120,
                },
                {
                    text: "계약담당자",
                    value: "measurer.name",
                    width: 100,
                },
                {
                    text: "업체명",
                    value: "latestDocument.employee.businessName",
                    width: 120,
                },
                {
                    text: "고객명",
                    value: "meta.employer.name",
                    width: 120,
                },
                {
                    text: "고객연락처",
                    value: "meta.employer.phone",
                    width: 140,
                },
                {
                    text: "가상계좌",
                    value: "virtualAccount",
                    width: 210,
                },
                {
                    text: "입금액",
                    value: "paymentStatus",
                    width: 180,
                },
                {
                    text: "증빙발급상태",
                    value: "taxProofStatus",
                    width: 110,
                },
                {
                    text: "정산상태",
                    value: "settlementStatus",
                    width: 80,
                },
                {
                    text: "Actions",
                    value: "actions",
                    width: 100,
                },
            ],
            contracts: [],
        };
    },
    computed: {
        payload() {
            return this.$store.state.payload || {};
        },
        scope() {
            return this.payload.scope || [];
        },
        isConsole() {
            return this.scope.includes("console");
        },
        isInterior() {
            return this.scope.includes("interior");
        },
        _user() {
            return this.payload._user;
        },
    },
    created() {
        this.init();
    },
    watch: {
        filter() {
            this.search();
        },
    },
    methods: {
        async init() {
            this.search(false);
        },
        async search(routable = true) {
            try {
                if (routable) {
                    this.$router.push({
                        query: Object.assignDefined({}, this.filter, {
                            page: this.page,
                        }),
                    });
                }

                if (this.isInterior) this.filter._interiors = this._user; // 인테리어 업체 필터링

                const { summary, contracts } = await api.console.contracts.getContracts({
                    headers: {
                        skip: (this.page - 1) * this.limit,
                        limit: this.limit,
                    },
                    params: this.filter,
                });

                this.contracts = contracts;

                this.pageCount = Math.ceil(summary.totalCount / this.limit);
            } catch (error) {
                console.error(error);
                if (error.response) alert(error.response.data.message);
                else alert(error.message.toString());
            }
        },
        edit(contract) {
            this.$router.push({
                path: `${this.$route.path}/${contract._id}`,
            });
        },
        create() {
            this.$router.push(`${this.$route.path}/write`);
        },
        getVirtualAccount({ virtualAccount = {} }) {
            const { bankNumber, accountNumber } = virtualAccount;

            const bankName = (() => {
                switch (bankNumber) {
                    case "020":
                        return "우리은행";
                    default:
                        return "-";
                }
            })();

            return `${bankName} - ${accountNumber}`;
        },
        getTaxProofStatus(contract) {
            const { transactions, taxProofs } = contract?.virtualAccount?.transactions?.reduce(
                ({ transactions, taxProofs }, { _taxProof }) => ({ transactions: transactions + 1, taxProofs: taxProofs + (_taxProof ? 1 : 0) }),
                {
                    transactions: 0,
                    taxProofs: 0,
                }
            );

            return `${taxProofs} / ${transactions}`;
        },
        getSettlementStatus(contract) {
            const { transactions, settlements } = contract?.virtualAccount?.transactions?.reduce(
                ({ transactions, settlements }, { _settlement }) => ({ transactions: transactions + 1, settlements: settlements + (_settlement ? 1 : 0) }),
                {
                    transactions: 0,
                    settlements: 0,
                }
            );

            return `${settlements} / ${transactions}`;
        },
    },
};
</script>
